import React, { useCallback, useMemo } from "react";
import styles from "./Sekeleton.module.css";
import { LucideIcon } from "../../icon/LucideIcon";
import BrandLoader from "../Spinner/BrandLoader";
import BrandIcon from "../Spinner/BrandIcon";
/**
 * This component returns an animated skeleton in different styles to be used as loading indications
 * Author: CS
 * @param: type
 * @returns: JSX.Element
 */

export interface SkeletonProps {
  type: "pairs" | "rows" | "columns" | "viewer" | "elnpage" | "filters";
  loading?: boolean;
  loadingProgress?: number;
  n?: number;
  buttons?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}
export const Skeleton = ({
  type = "pairs",
  loading = true,
  loadingProgress,
  n = 3,
  buttons = false,
  children,
  style,
}: SkeletonProps) => {
  const rows = useMemo(
    () => (
      <>
        <div className={`skeleton-block txt-body`} />
        <div className={`skeleton-block second txt-body`} />
        <div className={`skeleton-block third txt-body`} />
      </>
    ),
    []
  );

  const renderLoader = useCallback(
    (type: SkeletonProps["type"]) => {
      switch (type) {
        case "filters":
          return (
            <div className={styles.filters_container}>
              <div className={`skeleton-block ${styles.filter_form_element}`} style={{ height: 32, width: "80%" }} />
              <div
                className={`skeleton-block ${styles.filter_form_knob}`}
                style={{ width: 24, height: 24, borderRadius: "50%" }}
              />
              <div
                className={`skeleton-block second ${styles.filter_form_element}`}
                style={{ height: 32, width: "90%" }}
              />
              <div
                className={`skeleton-block second ${styles.filter_form_knob}`}
                style={{ width: 48, height: 24, borderRadius: "10px" }}
              />
              <div
                className={`skeleton-block third ${styles.filter_form_element}`}
                style={{ height: 32, width: "70%" }}
              />
              <div
                className={`skeleton-block third ${styles.filter_form_knob}`}
                style={{ width: 24, height: 24, borderRadius: "50%" }}
              />
              <div
                className={`skeleton-block btn-xs ${styles.filter_form_btn}`}
                style={{ width: "100%", height: 32, marginTop: "auto" }}
              />
            </div>
          );
        case "pairs":
          return (
            <div className={styles.pairs_container}>
              <div className={`skeleton-block txt-body`}></div>
              <div className={`skeleton-block txt-body`}></div>
              <div className={`skeleton-block second txt-body`}></div>
              <div className={`skeleton-block second txt-body`}></div>
              <div className={`skeleton-block third txt-body`}></div>
              <div className={`skeleton-block third txt-body`}></div>
            </div>
          );
        case "rows":
          return <div className={styles.rows_container}>{rows}</div>;
        case "columns":
          return (
            <React.Fragment>
              {buttons ? (
                <div className={styles.column_container}>
                  {[...Array(n - 1)].map((d, i) => (
                    <div className={styles.rows_container} key={i}>
                      {rows}
                    </div>
                  ))}
                  <div className={styles.rows_container}>
                    <div className={`skeleton-block btn-xs`}></div>
                    <div className={`skeleton-block second btn-xs`}></div>
                    <div className={`skeleton-block third btn-xs`}></div>
                  </div>
                </div>
              ) : (
                <div className={styles.column_container}>
                  {[...Array(n)].map((d, i) => (
                    <div className={styles.rows_container} key={i}>
                      {rows}
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        case "viewer":
          return (
            <div className={styles.viewer_skeleton}>
              <div className={styles.panel_left}>
                <div className={styles.panel_controls}>
                  <div></div>
                  <div></div>
                </div>
                <div className={styles.panel_left_bg}>
                  {loading ? (
                    loadingProgress ? (
                      <BrandIcon
                        size={"50%"}
                        style={{
                          maskImage: `linear-gradient(to top, black ${loadingProgress}%, transparent ${loadingProgress}%)`,
                          WebkitMaskImage: `linear-gradient(to top, black ${loadingProgress}%, transparent ${loadingProgress}%)`,
                          opacity: loadingProgress / 100,
                        }}
                      />
                    ) : (
                      <BrandLoader size={"50%"} style={{ opacity: "0.3" }} />
                    )
                  ) : (
                    <LucideIcon name="image-off" />
                  )}
                </div>
                {/* <div className={styles.viewer_content}>{children}</div> */}
              </div>
              <div className={styles.panel_right_top}>
                <div className={styles.panel_container}>
                  <div className="skeleton-block" style={{ ...(!loading && { animation: "none" }) }}></div>
                  <div className="skeleton-block second" style={{ ...(!loading && { animation: "none" }) }}></div>
                  <div className="skeleton-block third" style={{ ...(!loading && { animation: "none" }) }}></div>
                </div>
              </div>
              <div className={styles.panel_right_bottom}>
                <div className={styles.panel_pairs_container}>
                  <div className="skeleton-block" style={{ ...(!loading && { animation: "none" }) }}></div>
                  <div className="skeleton-block" style={{ ...(!loading && { animation: "none" }) }}></div>
                  <div className="skeleton-block second" style={{ ...(!loading && { animation: "none" }) }}></div>
                  <div className="skeleton-block second" style={{ ...(!loading && { animation: "none" }) }}></div>
                  <div className="skeleton-block third" style={{ ...(!loading && { animation: "none" }) }}></div>
                  <div className="skeleton-block third" style={{ ...(!loading && { animation: "none" }) }}></div>
                </div>
              </div>
              <div className={styles.viewer_content}>{children}</div>
            </div>
          );
        case "elnpage":
          return (
            <div className={styles.page} style={{ opacity: children ? 1 : 0.5 }}>
              {children !== undefined ? (
                <div>{children}</div>
              ) : (
                <div className={styles.pairs_container}>
                  <div className={`skeleton-block txt-body`}></div>
                  <div className={`skeleton-block txt-body`}></div>
                  <div className={`skeleton-block second txt-body`}></div>
                  <div className={`skeleton-block second txt-body`}></div>
                  <div className={`skeleton-block third txt-body`}></div>
                  <div className={`skeleton-block third txt-body`}></div>
                </div>
              )}
            </div>
          );
        default:
          return null;
      }
    },
    [buttons, children, loading, loadingProgress, n, rows]
  );

  return (
    <div className={styles.container} style={style}>
      {renderLoader(type)}
    </div>
  );
};
